import { db, storage } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

export interface AboutContent {
  companyDescription: string;
  teamDescription: string;
  valuesTitle: string;
  valuesList: string[];
  processTitle: string;
  processDescription: string;
  processList: string[];
  profileImage: string | null;
}

class AboutService {
  private readonly DOCUMENT_ID = 'about_content';
  private readonly COLLECTION = 'settings';
  private readonly STORAGE_PATH = 'about/';
  
  // Get the about page content
  async getAboutContent(): Promise<AboutContent> {
    try {
      const docRef = doc(db, this.COLLECTION, this.DOCUMENT_ID);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        // Return default values if no about content exists yet
        return this.getDefaultAboutContent();
      }
      
      return docSnap.data() as AboutContent;
    } catch (error) {
      console.error('Error fetching about content:', error);
      return this.getDefaultAboutContent();
    }
  }
  
  // Update the about page content
  async updateAboutContent(aboutContent: AboutContent): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, this.DOCUMENT_ID);
      await setDoc(docRef, aboutContent);
    } catch (error) {
      console.error('Error updating about content:', error);
      throw error;
    }
  }
  
  // Upload profile image
  async uploadProfileImage(file: File): Promise<string> {
    try {
      // Create a reference to the profile image in Firebase Storage
      const storageRef = ref(storage, `${this.STORAGE_PATH}profile-image`);
      
      // Upload the file
      await uploadBytes(storageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update the about content with the new profile image URL
      const aboutContent = await this.getAboutContent();
      await this.updateAboutContent({
        ...aboutContent,
        profileImage: downloadURL
      });
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading profile image:', error);
      throw error;
    }
  }
  
  // Delete profile image
  async deleteProfileImage(): Promise<void> {
    try {
      const aboutContent = await this.getAboutContent();
      
      // If there's a profile image, delete it from storage
      if (aboutContent.profileImage) {
        // Create a reference to the profile image in Firebase Storage
        const storageRef = ref(storage, `${this.STORAGE_PATH}profile-image`);
        
        // Delete the file
        await deleteObject(storageRef);
        
        // Update the about content with no profile image
        await this.updateAboutContent({
          ...aboutContent,
          profileImage: null
        });
      }
    } catch (error) {
      console.error('Error deleting profile image:', error);
      throw error;
    }
  }
  
  // Default about page content
  private getDefaultAboutContent(): AboutContent {
    return {
      companyDescription: 'Ridgeline Homes is a premium residential homebuilder dedicated to creating exceptional living spaces that exceed our clients\' expectations. With years of experience in the industry, we\'ve built our reputation on quality craftsmanship, attention to detail, and personalized service.',
      teamDescription: 'Our team of skilled professionals works closely with each client to understand their vision and bring it to life. We believe that building a home is more than just construction – it\'s about creating a space where memories will be made and lives will be lived.',
      valuesTitle: 'Our Values',
      valuesList: [
        'Quality craftsmanship in every detail',
        'Personalized service and attention',
        'Transparent communication',
        'Sustainable building practices',
        'Customer satisfaction'
      ],
      processTitle: 'Our Process',
      processDescription: 'We follow a comprehensive process to ensure your dream home becomes a reality:',
      processList: [
        'Initial consultation to understand your vision',
        'Detailed planning and design phase',
        'Material selection and customization',
        'Construction with regular updates',
        'Final walkthrough and quality assurance'
      ],
      profileImage: null
    };
  }
}

export const aboutService = new AboutService(); 